import { Injectable } from '@angular/core';

/** A free-drag offset from the toolbar's resting position, in pixels. */
export interface ToolbarOffset {
  x: number;
  y: number;
}

/**
 * Where the user has dragged the on-map toolbar.
 *
 * Root-scoped and deliberately NOT per-bar: the map shows one toolbar at a time
 * and swaps which one — live to snapshot playback, live to a vessel track — as
 * the user works. Each swap destroys the host component and builds a new one, so
 * a position held on the component would snap the bar back to the bottom centre
 * every time the operator traced a vessel. Holding it here is what makes the bar
 * stay where it was put.
 *
 * One shared offset is correct rather than merely convenient: every wrapper the
 * bar mounts into (`.ais-live-controls`, `.ais-playback-overlay`) anchors it to
 * the same place — a full-width strip 24px off the bottom, centre-aligned — so
 * the same delta means the same point on the map in all of them.
 *
 * The offset is per session, not persisted: it describes a viewport the next
 * visit may not have.
 */
@Injectable({ providedIn: 'root' })
export class ToolbarDragService {
  /**
   * Mutated in place rather than replaced, because it is bound straight to
   * `cdkDragFreeDragPosition`. CDK re-applies the transform when that binding's
   * REFERENCE changes, which on a live bar would fight the drag in progress; a
   * new host reads the current value on init, which is the only moment the
   * restore has to happen.
   */
  readonly position: ToolbarOffset = { x: 0, y: 0 };

  /** True once the bar has been moved off its resting position. */
  get moved(): boolean {
    return this.position.x !== 0 || this.position.y !== 0;
  }

  set(offset: ToolbarOffset): void {
    this.position.x = offset.x;
    this.position.y = offset.y;
  }

  /** Send the bar back to where it started (the grip's double-click). */
  reset(): void {
    this.position.x = 0;
    this.position.y = 0;
  }
}
